import { useMemo, useState } from 'react';
import { Avatar, Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { authService } from 'framework-react-core';

interface UserProfileMenuProps {
  name?: string;
  avatarUrl?: string | null;
}

function getInitials(name?: string): string {
  if (!name) return 'U';

  const formattedName = name.includes(',')
    ? name.split(',').reverse().join(' ').trim()
    : name.trim();

  const splitWords = formattedName.split(/\s+/).filter(Boolean);
  return (
    splitWords
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'U'
  );
}

export default function UserProfileMenu({ name, avatarUrl }: UserProfileMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const initials = useMemo(() => getInitials(name), [name]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();

    if (authService?.msalClient) {
      await authService.msalClient.logoutRedirect();
      return;
    }

    window.location.assign('/');
  };

  return (
    <Box sx={{ position: 'absolute', top: 6, left: '90%', zIndex: 1400 }}>
      <Tooltip title={name || 'User profile'} arrow>
        <IconButton
          onClick={handleOpen}
          size="small"
          aria-label="Open user settings"
          sx={{ p: 0.25 }}
        >
          <Avatar
            src={avatarUrl ?? undefined}
            alt={name || 'User'}
            sx={{ width: 32, height: 32, fontSize: 12 }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
      </Menu>
    </Box>
  );
}
